import { LangLocale, languages } from '@myre/common-i18n';
import {
  COLOR_NEUTRAL_TWO,
  COLOR_PRIMARY,
  COLOR_QUATERNARY_PALE,
  COLOR_SECONDARY,
  COLOR_TERTIARY,
  Z_INDEX_SPINNER,
} from '@myre/common-styles';
import { LANGLOCALE } from '@myre/ui-primitive-core';

import { DOCUMENT, PercentPipe } from '@angular/common';
import {
  Directive,
  ElementRef,
  inject,
  Input,
  NgZone,
  Renderer2,
  type AfterViewInit,
  type DoCheck,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { formatDuration } from 'date-fns';
import { castArray } from 'lodash-es';

const ANIMATION_ROTATE_NAME = 'myreSpinLoaderRotation';
const ANIMATION_OPACITY_NAME = 'myreSpinLoaderOpacity';
const STYLE_ID = 'myre-spin-loader-style';
const OPACITY_FADE_TIMING = 150;
const MAX_SPINNER_SIZE = 40;

/**
 * A directive that displays a spinning loader.
 *
 * @dynamic
 */
@Directive({ selector: '[myreSpinLoader]' })
export class SpinLoaderDirective implements AfterViewInit, DoCheck {
  private readonly _element = inject(ElementRef);
  private readonly _ngZone = inject(NgZone);
  private readonly _percentPipe = inject(PercentPipe);
  private readonly _renderer = inject(Renderer2);
  private readonly _translateService = inject(TranslateService);
  private _document = inject<Document>(DOCUMENT);
  private readonly _langLocale = inject<LangLocale>(LANGLOCALE);

  @Input()
  public color: 'green' | 'yellow' | 'red' | 'blue' | 'grey' = 'blue';

  @Input()
  public set hasBackground(hasBackGround: boolean) {
    if (!this._overlayContainerElement) {
      return;
    }

    this._renderer.setStyle(
      this._overlayContainerElement,
      'background-color',
      hasBackGround ? '#ffffff' : 'none',
    );

    this._hasBackground = hasBackGround;
  }

  @Input()
  public set myreSpinLoader(value: boolean) {
    this._ngZone.runOutsideAngular(() => {
      if (value) {
        this._renderer.setStyle(this._spinnerElement, 'display', 'flex');

        setTimeout(() => {
          this._renderer.setStyle(
            this._overlayContainerElement,
            'opacity',
            this._hasBackground ? 0.75 : 1,
          );
        });
      } else {
        setTimeout(() => {
          this._renderer.setStyle(
            this._overlayContainerElement,
            'display',
            'none',
          );
        }, OPACITY_FADE_TIMING);

        this._renderer.setStyle(this._overlayContainerElement, 'opacity', 0);
      }
    });
  }

  @Input()
  public set myreSpinPercentage(percentage: number | null) {
    this._percentageElement = this._manageContextElement(
      this._percentageElement,
      percentage,
      {
        formatValue: value =>
          `${this._translateService.instant(
            'loadingRecords',
          )} ${this._percentPipe.transform(value)}`,
        class: 'myre-spinner__percentage',
      },
    );
  }

  @Input()
  public set myreSpinMessage(label: string | null) {
    this._labelElement = this._manageContextElement(this._labelElement, label, {
      class: ['myre-spinner__label', 'myre-spinner--small-margin'],
    });
  }

  @Input()
  public set myreSpinRemainingTaskTime(time: number | null) {
    this._timeElement = this._manageContextElement(this._timeElement, time, {
      formatValue: value => `⏳ ${this._formatTime(value)}`,
      class: 'myre-spinner__time',
    });
  }

  private _overlayContainerElement: HTMLDivElement;
  private _hasBackground: boolean;

  private _spinnerElement: HTMLDivElement;
  private _contextContainerElement: HTMLDivElement;

  private _labelElement: HTMLDivElement;
  private _timeElement: HTMLDivElement;
  private _percentageElement: HTMLDivElement;

  constructor() {
    this._createOverlay();
    this._createSpinner();
    this._createContext();

    this._createStyle();
    this.myreSpinLoader = true;
  }

  public ngAfterViewInit(): void {
    this._position();
  }

  public ngDoCheck(): void {
    this._position();
  }

  private _position(): void {
    const elWidth = this._element.nativeElement.offsetWidth;
    const elHeight = this._element.nativeElement.offsetHeight;
    const top = this._element.nativeElement.offsetTop;
    const left = this._element.nativeElement.offsetLeft;

    this._renderer.setStyle(
      this._overlayContainerElement,
      'width',
      elWidth + 'px',
    );
    this._renderer.setStyle(
      this._overlayContainerElement,
      'height',
      elHeight + 'px',
    );
    this._renderer.setStyle(
      this._overlayContainerElement,
      'position',
      'absolute',
    );
    this._renderer.setStyle(this._overlayContainerElement, 'top', top + 'px');
    this._renderer.setStyle(this._overlayContainerElement, 'left', left + 'px');

    this._adjustSpinnerSize(Math.min(elWidth, elHeight) / 2);
  }

  private _createSpinner(): void {
    const parentElement = this._renderer.createElement('div');
    this._renderer.addClass(parentElement, 'myre-spinner__container');
    this._renderer.appendChild(this._overlayContainerElement, parentElement);

    this._spinnerElement = this._renderer.createElement('div');
    const animation = `${ANIMATION_ROTATE_NAME} 1500ms linear infinite, ${ANIMATION_OPACITY_NAME} 3000ms ease-in-out infinite`;
    this._renderer.setStyle(this._spinnerElement, 'animation', animation);
    this._renderer.appendChild(parentElement, this._spinnerElement);
  }

  private _createContext(): void {
    this._contextContainerElement = this._renderer.createElement('div');
    this._renderer.addClass(
      this._contextContainerElement,
      'myre-spinner__context',
    );
    this._renderer.appendChild(
      this._overlayContainerElement,
      this._contextContainerElement,
    );
  }

  private _createStyle(): void {
    let s = this._document.getElementById(STYLE_ID);

    if (s) {
      return;
    }

    s = this._renderer.createElement('style');
    this._renderer.setAttribute(s, 'id', STYLE_ID);

    s.innerHTML = `
      .myre-spinner__overlay {
        display: block;
        width: 100%;
        padding: 16px;
      }

      .myre-spinner__container {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .myre-spinner__context {
        display: flex;
        flex-direction: column;
        align-items: center;

        font-family: Roboto;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;

        color: #596277;
      }

      .myre-spinner__percentage {
        order: 1;
      }

      .myre-spinner__label {
        order: 2;
      }

      .myre-spinner__time {
        order: 3;
      }

      .myre-spinner__context > *:not(.myre-spinner--small-margin) {
        margin-top: 16px;
       }

      .myre-spinner--small-margin {
        margin-top: 4px;
       }

      @keyframes ${ANIMATION_ROTATE_NAME} {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      @keyframes ${ANIMATION_OPACITY_NAME} {
        0% {
          opacity: 1;
        }
        50% {
          opacity: 0.8;
        }
        100% {
          opacity: 1;
        }
      }
    `;

    this._renderer.appendChild(this._document.head, s);
  }

  private _adjustSpinnerSize(size: number): void {
    // limit spinner size when information is displayed to respect design guidelines
    if (this._labelElement || this._timeElement || this._percentageElement) {
      size = Math.min(size, MAX_SPINNER_SIZE);
    }

    this._renderer.setStyle(this._spinnerElement, 'width', size + 'px');
    this._renderer.setStyle(this._spinnerElement, 'height', size + 'px');

    const borderWidth = Math.trunc(size / 10);

    this._renderer.setStyle(
      this._spinnerElement,
      'border',
      `${borderWidth}px solid transparent`,
    );
    this._renderer.setStyle(this._spinnerElement, 'border-radius', '50%');

    let borderColor: string;

    switch (this.color) {
      case 'green': {
        borderColor = COLOR_PRIMARY;

        break;
      }
      case 'red': {
        borderColor = COLOR_SECONDARY;

        break;
      }
      case 'blue': {
        borderColor = COLOR_TERTIARY;

        break;
      }
      case 'yellow': {
        borderColor = COLOR_QUATERNARY_PALE;

        break;
      }
      case 'grey': {
        borderColor = COLOR_NEUTRAL_TWO;

        break;
      }
    }

    this._renderer.setStyle(
      this._spinnerElement,
      'border-top-color',
      borderColor,
    );
    this._renderer.setStyle(
      this._spinnerElement,
      'border-bottom-color',
      borderColor,
    );
  }

  private _createOverlay(): void {
    this._overlayContainerElement = this._renderer.createElement('div');
    this._renderer.addClass(
      this._overlayContainerElement,
      'myre-spinner__overlay',
    );
    this.hasBackground = false;
    this._renderer.setStyle(
      this._overlayContainerElement,
      'z-index',
      Z_INDEX_SPINNER,
    );
    this._renderer.setStyle(
      this._overlayContainerElement,
      'transition',
      `opacity ${OPACITY_FADE_TIMING}ms ease-in-out`,
    );
    this._renderer.appendChild(
      this._element.nativeElement,
      this._overlayContainerElement,
    );
  }

  private _manageContextElement(
    element: HTMLDivElement,
    value: number | string | null,
    option: {
      formatValue?: (value: any) => string;
      class?: string | string[];
    },
  ): HTMLDivElement {
    if (value === null) {
      if (element) {
        this._renderer.removeChild(this._contextContainerElement, element);
      }

      return null;
    }

    if (!element) {
      element = this._renderer.createElement('div');

      if (option?.class) {
        for (const className of castArray(option.class)) {
          this._renderer.addClass(element, className);
        }
      }

      this._renderer.appendChild(this._contextContainerElement, element);
    }

    element.innerHTML = option?.formatValue
      ? option.formatValue(value)
      : value.toString();

    return element;
  }

  private _formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    return formatDuration(
      {
        hours,
        minutes,
        seconds: remainingSeconds,
      },
      {
        delimiter: ' ',
        zero: false,
        format:
          hours || minutes
            ? ['hours', 'minutes']
            : ['hours', 'minutes', 'seconds'],
        locale: languages[this._langLocale].dateFnsLocale,
      },
    );
  }
}
